/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Challenge Type Selection Component - Select and start challenges
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import useChallengeStore from '../hooks/useChallenge'
import { useAuthStore } from '../stores/authStore'
import {
  BookOpen,
  Calculator,
  Clock,
  Target,
  Info,
  Sparkles,
  AlertTriangle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import ActiveChallengeWarningModal from '../components/ActiveChallengeWarningModal'
import challengeService from '../services/challengeService'
import toast from 'react-hot-toast'
import { getChallengeSessionPath } from '../utils/navigation'
import { logError } from '../utils/errorUtils'
import { createLogger } from '../utils/logger'

const logger = createLogger('ChallengeTypeSelection')

const ChallengeTypeSelection = () => {
  const navigate = useNavigate()
  const {
    createAndStartChallenge,
    isLoading,
    initializeChallengeStore,
    currentChallenge,
    cancelChallenge,
    stages
  } = useChallengeStore()
  const { user } = useAuthStore()

  const [availableStrategies, setAvailableStrategies] = useState([])
  const [isCreating, setIsCreating] = useState(false)
  const [startingStrategy, setStartingStrategy] = useState(null)
  const [testModeEnabled, setTestModeEnabled] = useState({})
  const [testModeUsedToday, setTestModeUsedToday] = useState(false)
  const [isLoadingStrategies, setIsLoadingStrategies] = useState(true)
  const [showActiveWarningModal, setShowActiveWarningModal] = useState(false)
  const [activeChallengData, setActiveChallengeData] = useState(null)
  const [pendingStrategy, setPendingStrategy] = useState(null)

  useEffect(() => {
    initializeChallengeStore()
      .then((result) => {
        if (result.success && result.hasActiveChallenge) {
          logger.debug('✅ Active challenge found')
        }
      })
      .catch((error) => {
        logger.error('❌ Failed to initialize challenge store:', error)
      })

    loadAvailableStrategies()
    loadTestModeStatus()
  }, [initializeChallengeStore])

  const loadTestModeStatus = async () => {
    try {
      const result = await challengeService.getTestModeStatus()
      if (result.success) {
        setTestModeUsedToday(result.data.hasUsedTestModeToday)
      }
    } catch (error) {
      logger.error('Failed to check test mode status:', error)
      setTestModeUsedToday(true)
    }
  }

  const getIconForStrategyType = (strategyType) => {
    const iconMap = {
      SAT_MATH: Calculator,
      SAT_MATH_MINI: Calculator,
      SAT_READING_WRITING: BookOpen,
      SAT_READING_WRITING_MINI: BookOpen
    }
    return iconMap[strategyType] || Sparkles
  }

  const getColorForStrategyType = (strategyType) => {
    const colorMap = {
      SAT_MATH: 'blue',
      SAT_MATH_MINI: 'blue',
      SAT_READING_WRITING: 'green',
      SAT_READING_WRITING_MINI: 'green'
    }
    return colorMap[strategyType] || 'blue'
  }

  const calculateEstimatedTime = (totalQuestions) => {
    if (!totalQuestions) return '20 min'
    const minutes = totalQuestions * 2
    return minutes >= 60 ? `${Math.round(minutes / 5) * 5} min` : `${minutes} min`
  }

  const getStageDescription = (stageType) => {
    const descriptionMap = {
      EXPLORATORY: 'Fixed questions with free navigation',
      ADAPTIVE: 'Dynamic questions based on performance',
      SIMPLE: 'Basic question answering'
    }
    return descriptionMap[stageType] || 'Question answering'
  }

  const loadAvailableStrategies = async () => {
    try {
      setIsLoadingStrategies(true)
      const response = await challengeService.getAvailableStrategies()

      if (!response.success || !response.data) {
        throw new Error('Failed to load strategies from API')
      }

      const strategies = response.data
        .filter((strategy) => {
          if (import.meta.env.PROD && strategy.description?.toLowerCase().includes('dev only')) {
            return false
          }
          return true
        })
        .map((strategy) => {
          const stages = strategy.stageFactories.map((stageFactory) => ({
            type: stageFactory.type,
            questionCount: stageFactory.questionCount,
            description: getStageDescription(stageFactory.type)
          }))

          const totalQuestions = strategy.questionCounts?.total || null

          return {
            type: strategy.strategyType,
            name: strategy.name,
            description: strategy.description,
            icon: getIconForStrategyType(strategy.strategyType),
            color: getColorForStrategyType(strategy.strategyType),
            stages,
            totalQuestions,
            estimatedTime: calculateEstimatedTime(totalQuestions),
            domainDistributions: strategy.domainDistributions,
            questionCounts: strategy.questionCounts
          }
        })

      logger.debug('✅ Strategies loaded and mapped:', strategies)
      setAvailableStrategies(strategies)
    } catch (error) {
      logger.error('❌ Error loading strategies:', error)
      logError('Failed to load challenge types', {
        type: 'challenge_ui',
        action: 'loadStrategies',
        error: error.message,
        stack: error.stack
      })
      toast.error('Failed to load challenge types')
    } finally {
      setIsLoadingStrategies(false)
    }
  }

  const isFullLengthStrategy = (strategyType) => {
    return strategyType === 'SAT_MATH' || strategyType === 'SAT_READING_WRITING'
  }

  const handleInlineStart = async (strategy) => {
    if (currentChallenge) {
      logger.debug('🔄 Active challenge found, showing warning modal')
      const completedStages = stages?.filter((s) => s.status === 'completed').length || 0
      const totalStages = stages?.length || 0
      const currentStage = stages?.find((s) => s.status === 'in_progress')

      setActiveChallengeData({
        challengeName: currentChallenge.name || currentChallenge.strategyType || 'Challenge',
        currentStageName: currentStage?.name || 'In Progress',
        completedStages,
        totalStages
      })

      setPendingStrategy(strategy)
      setShowActiveWarningModal(true)
      return
    }

    await startChallengeForStrategy(strategy)
  }

  const startChallengeForStrategy = async (strategy) => {
    setIsCreating(true)
    setStartingStrategy(strategy.type)

    try {
      const isTestMode = testModeEnabled[strategy.type] && isFullLengthStrategy(strategy.type)
      const challengeData = {
        strategyType: strategy.type,
        ...(isTestMode && { mode: 'test' })
      }

      logger.debug('🚀 Creating and starting challenge with data:', challengeData)

      const result = await createAndStartChallenge(challengeData)

      if (result.success) {
        logger.debug('✅ Challenge created and started successfully')
        toast.success('Challenge started successfully!')
        navigate(getChallengeSessionPath(result.challenge?.id), { replace: true })
      } else {
        logger.error('❌ Failed to create challenge:', result.error)
        logError('Challenge creation failed in UI', {
          type: 'challenge_ui',
          action: 'handleInlineStart',
          error: result.error,
          challengeData,
          strategy
        })
        toast.error(result.error || 'Failed to start challenge')
      }
    } catch (error) {
      logger.error('Error starting challenge:', error)
      logError('Challenge creation exception in UI', {
        type: 'challenge_ui',
        action: 'handleInlineStart',
        error: error.message,
        stack: error.stack
      })
      toast.error('Failed to start challenge')
    } finally {
      setIsCreating(false)
      setStartingStrategy(null)
    }
  }

  const handleResumeActiveChallenge = () => {
    setShowActiveWarningModal(false)
    navigate(getChallengeSessionPath(currentChallenge?.id), { replace: true })
  }

  const handleCancelActiveChallenge = async () => {
    if (!currentChallenge) return

    try {
      const result = await cancelChallenge(currentChallenge.id)

      if (result.success) {
        toast.success('Challenge cancelled successfully')
        setShowActiveWarningModal(false)
        setActiveChallengeData(null)
        if (pendingStrategy) {
          const strategy = pendingStrategy
          setPendingStrategy(null)
          await startChallengeForStrategy(strategy)
        }
      } else {
        toast.error(result.error || 'Failed to cancel challenge')
      }
    } catch (error) {
      logger.error('Error cancelling challenge:', error)
      toast.error('Failed to cancel challenge')
    }
  }

  const getIconBgClass = (color) => {
    return color === 'green'
      ? 'bg-gradient-to-br from-emerald-500 to-green-600'
      : 'bg-gradient-to-br from-blue-500 to-indigo-600'
  }

  // Unlike the icon backdrop (which keeps a subject color for at-a-glance
  // identification), the actual CTA button should track the org's brand
  // color like every other "Start"/"Continue" action in the app — not vary
  // by subject.
  const getButtonClass = () =>
    'bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600'

  if (isLoading || isLoadingStrategies) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <LoadingSpinner size="large" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">Loading challenge types...</p>
        </div>
      </div>
    )
  }

  const fullStrategies = availableStrategies.filter((s) => isFullLengthStrategy(s.type))
  const miniStrategies = availableStrategies.filter((s) => !isFullLengthStrategy(s.type))

  const renderRow = (strategy) => {
    const IconComponent = strategy.icon
    const isStarting = startingStrategy === strategy.type
    const isMini = !isFullLengthStrategy(strategy.type)

    return (
      <div
        key={strategy.type}
        className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex items-center gap-4 hover:shadow-md transition-shadow duration-200"
      >
        {/* Icon */}
        <div
          className={`flex-shrink-0 flex items-center justify-center w-12 h-12 rounded-xl ${getIconBgClass(strategy.color)} text-white`}
        >
          <IconComponent className="w-6 h-6" />
        </div>

        {/* Name + description */}
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 dark:text-gray-100">{strategy.name}</div>
          {strategy.description && (
            <div className="hidden md:block text-sm text-gray-500 dark:text-gray-400">
              {strategy.description}
            </div>
          )}
        </div>

        {/* Meta pills */}
        <div className="hidden sm:flex items-center gap-2 flex-shrink-0">
          {strategy.totalQuestions !== null && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              <Target className="w-3 h-3" />
              {strategy.totalQuestions} questions
            </span>
          )}
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            <Clock className="w-3 h-3" />
            {strategy.estimatedTime}
          </span>
        </div>

        {/* Test Mode toggle (full-length only) */}
        {!isMini && (
          <div className="hidden md:flex flex-col items-end gap-1 flex-shrink-0">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Test Mode</span>
              <button
                role="switch"
                aria-checked={testModeEnabled[strategy.type] || false}
                disabled={testModeUsedToday}
                onClick={() =>
                  setTestModeEnabled((prev) => ({
                    ...prev,
                    [strategy.type]: !prev[strategy.type]
                  }))
                }
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  testModeEnabled[strategy.type] ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
                } ${testModeUsedToday ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                    testModeEnabled[strategy.type] ? 'translate-x-4' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
            {testModeEnabled[strategy.type] && !testModeUsedToday && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="w-3 h-3" />
                Timed · 1 per day
              </span>
            )}
            {testModeUsedToday && (
              <span className="text-xs text-gray-400">Limit reached today</span>
            )}
          </div>
        )}

        {/* Start button */}
        <button
          onClick={() => handleInlineStart(strategy)}
          disabled={isStarting || isCreating}
          className={`flex-shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold text-white ${getButtonClass(strategy.color)} disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-w-[130px] justify-center`}
        >
          {isStarting ? (
            <>
              <LoadingSpinner size="small" />
              Starting…
            </>
          ) : (
            <>
              Start Challenge
              <ChevronRight className="w-4 h-4" />
            </>
          )}
        </button>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Back link */}
        <button
          onClick={() => navigate('/dashboard')}
          className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 mb-8 transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Start a Challenge</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm">
            Choose what you want to practice. Short on time? Try a Mini session.
          </p>
        </div>

        {availableStrategies.length === 0 ? (
          <div className="text-center py-16 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-100 dark:bg-gray-700 rounded-full mb-4">
              <Info className="w-7 h-7 text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
              No Challenge Types Available
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Please contact your administrator.
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {fullStrategies.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
                  Full Practice
                </h2>
                <div className="space-y-3">{fullStrategies.map(renderRow)}</div>
              </section>
            )}

            {miniStrategies.length > 0 && (
              <section>
                <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500 mb-3">
                  Quick Sessions
                </h2>
                <div className="space-y-3">{miniStrategies.map(renderRow)}</div>
              </section>
            )}
          </div>
        )}
      </div>

      <ActiveChallengeWarningModal
        isOpen={showActiveWarningModal}
        onClose={() => setShowActiveWarningModal(false)}
        onResume={handleResumeActiveChallenge}
        onCancel={handleCancelActiveChallenge}
        challengeData={activeChallengData}
        isProcessing={isLoading}
      />
    </div>
  )
}

export default ChallengeTypeSelection
